<template>
  <div class="user-menu">
    <i-menu
      v-if="currentUser"
      ref="menuEl"
      :close-on-item-click="true"
      :trigger-align="iMenuTriggerAlignOptions.RIGHT"
      :width="212"
      @i-menu-open-changed="onMenuOpenChanged"
    >
      <template #trigger>
        <button
          data-testid="user-button"
          :class="{
            open: menuOpen === true,
            collapsed,
            'i-p-6': !collapsed,
            'd-flex align-items-center justify-content-center': collapsed,
          }"
          class="user-button i-radius-2xl"
          aria-label="User menu: Toggle visibility"
          data-dd-action-name="user-menu-toggle"
          @mouseenter="hovering = true"
          @mouseleave="hovering = ''"
        >
          <div
            :class="{ 'align-items-center justify-content-center': collapsed }"
            class="user-container d-flex flex-row align-items-center"
          >
            <user-avatar
              :user="currentUser"
              :class="{ 'i-mr-5': !collapsed }"
            ></user-avatar>
            <div class="user-data d-flex flex-column align-items-start">
              <div
                class="name data text-neutral-bold i-text-button-3"
                :title="username"
              >
                {{ username }}
              </div>
              <div
                class="organisation data text-neutral i-text-body-3"
                :title="organisation"
              >
                {{ organisation }}
              </div>
            </div>
            <i-icon
              :symbol="menuOpen ? 'small-chevron-up' : 'small-chevron-down'"
              class="menu-icon"
              :colour="
                hovering || menuOpen
                  ? 'var(--icon-neutral)'
                  : 'var(--icon-neutral)'
              "
              :size="16"
            ></i-icon>
          </div>
        </button>
      </template>

      <template #items>
        <i-menu-item
          label="Account"
          aria-label="User menu: Go to account settings"
          data-dd-action-name="user-menu-account"
          icon="small-user-single"
          data-testid="user-menu-account"
          @item-click="() => openRoute('/settings/account')"
        ></i-menu-item>

        <i-menu-item
          label="Team"
          aria-label="User menu: Go to team settings"
          data-dd-action-name="user-menu-team"
          icon="small-user-multiple"
          data-testid="user-menu-team"
          @item-click="() => openRoute('/settings/team')"
        ></i-menu-item>

        <i-menu-item
          v-if="hasEditPermissions"
          label="Invite User"
          aria-label="User menu: Invite a new user"
          data-dd-action-name="user-menu-invite-user"
          icon="small-user-add"
          data-testid="user-menu-invite-user"
          @item-click="() => inviteUser()"
        ></i-menu-item>

        <i-menu-item
          label="Reset Onboarding"
          aria-label="User menu: Reset onboarding flow"
          data-dd-action-name="user-menu-reset-onboarding"
          icon="small-time-reset"
          data-testid="user-menu-reset-onboarding"
          @item-click="() => resetOnboardingBrowserState()"
        ></i-menu-item>

        <div class="divider"></div>

        <i-menu-item
          label="Help Center"
          aria-label="User menu: Open help center"
          data-dd-action-name="user-menu-help-center"
          icon="small-help-question-circle"
          data-testid="user-menu-help-center"
          right-icon="medium_link_square_1"
          :right-icon-color="'var(--icon-neutral)'"
          @item-click="() => openExternalLink('https://help.inscribe.ai/en/')"
        ></i-menu-item>

        <i-menu-item
          label="API Documentation"
          aria-label="User menu: Open API documentation"
          data-dd-action-name="user-menu-api-docs"
          icon="small-programming-browser-code-2"
          data-testid="user-menu-api-documentation"
          right-icon="medium_link_square_1"
          :right-icon-color="'var(--icon-neutral)'"
          @item-click="() => openExternalLink('https://docs.inscribe.ai')"
        ></i-menu-item>

        <div class="divider"></div>

        <!-- workspaces -->
        <i-menu-item
          label="Switch Workspace"
          aria-label="User menu: Open workspace switcher"
          data-dd-action-name="user-menu-workspace-switcher"
          icon="small-arrows-reload"
          data-testid="user-menu-switch-workspace"
          @item-click="() => workspaceSwitcherRef?.openSwitcherModal()"
        ></i-menu-item>

        <i-menu-item
          v-if="orgAdminAndCreationEnabled"
          label="New Workspace"
          aria-label="User menu: Create new workspace"
          data-dd-action-name="user-menu-new-workspace"
          icon="small-add-circle"
          data-testid="user-menu-new-workspace"
          @item-click="() => workspaceSwitcherRef?.openCreateModal()"
        ></i-menu-item>

        <div class="divider"></div>

        <i-menu-item
          label="Sign Out"
          aria-label="User menu: Sign out of account"
          data-dd-action-name="user-menu-sign-out"
          icon="small-logout"
          data-testid="user-menu-logout"
          @item-click="() => doLogout()"
        ></i-menu-item>
      </template>
    </i-menu>

    <!-- workspace panel  -->
    <workspace-switcher ref="workspaceSwitcherRef" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import {
  iIcon,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  showToast,
} from '@inscribe/signal';
import UserAvatar from '@/V2/components/UserAvatar';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { OnboardingService } from '@/shared/services/onboarding/onboarding.service';

export default {
  name: 'UserArea',
  components: {
    iIcon,
    iMenu,
    iMenuItem,
    UserAvatar,
    WorkspaceSwitcher,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    iMenuTriggerAlignOptions,
    hovering: false,
    menuOpen: false,
    workspaceSwitcherRef: null,
  }),
  mounted() {
    this.workspaceSwitcherRef = this.$refs.workspaceSwitcherRef;
  },
  computed: {
    ...mapGetters({
      orgAdminAndCreationEnabled: 'isOrgAdminAndWorkspacesCreationEnabled',
      hasEditPermissions: 'isOrgAdmin',
    }),
    ...mapState(['currentUser']),
    username() {
      if (this.currentUser.first_name || this.currentUser.last_name) {
        return `${this.currentUser.first_name} ${this.currentUser.last_name}`;
      } else {
        return this.currentUser.username;
      }
    },
    organisation() {
      return this.currentUser.organisation?.name || '';
    },
  },
  methods: {
    openRoute(path) {
      if (this.$route.fullPath !== path) {
        this.$router.push({
          path: path,
        });
      }
    },
    inviteUser() {
      const route = '/settings/team?showInviteModal=true';
      if (!this.$route.fullPath.includes('/settings/team')) {
        this.openRoute(route);
      } else {
        this.openRoute('/');
        setTimeout(() => {
          this.openRoute(route);
        });
      }
    },
    openExternalLink(url) {
      window.open(url, '_blank');
    },
    doLogout() {
      if (confirm('Are you sure you want to log out?')) {
        localStorage.removeItem('inscribeScopeSelectedUserDefault');
        this.$store.dispatch('logout').then(() => {
          this.$router.push({ name: 'login' });
        });
      }
    },
    onMenuOpenChanged(menuOpen) {
      this.menuOpen = menuOpen;
    },
    resetOnboardingBrowserState() {
      OnboardingService.resetOnboardingBrowserState();
      showToast({
        text: 'Onboarding reset successfully.',
        isSuccess: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.user-menu {
  user-select: none;

  :deep(.trigger-container) {
    display: flex;
    justify-content: center;
  }

  .user-button {
    width: 100%;
    height: 56px;
    border: 1px solid var(--border-neutral-subtle);
    background-color: var(--surface-neutral-subtle);
    outline: 0;
    transition: background-color 150ms ease-in-out;

    &:hover,
    &.open {
      background-color: var(--base);
    }

    &.collapsed {
      height: 34px;
      width: 34px;
      border-radius: 50%;

      .user-data,
      .menu-icon {
        display: none !important;
      }
    }

    .user-data {
      flex: 1;

      .data {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 120px;
      }
    }
  }
}
</style>
